import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import ChatWidget from '../components/site/ChatWidget';
import NewsletterBox from '../components/site/NewsletterBox';
import Seo, { SITE_URL, ORG_JSONLD, breadcrumbLd } from '../components/Seo';
import { fetchBlogPosts, fetchBlogPost } from '../lib/api';
import { Button } from '../components/ui/button';
import { ArrowLeft, ArrowRight, Clock, Loader2, Tag } from 'lucide-react';

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

// Minimal markdown renderer: ##/### headings, **bold**, - lists, paragraphs
const renderInline = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} className="font-bold text-slate-900">{part.slice(2, -2)}</strong>
      : part
  );

export const Markdown = ({ content }) => {
  const blocks = useMemo(() => (content || '').split(/\n\n+/), [content]);
  return (
    <div className="space-y-5">
      {blocks.map((block, bi) => {
        const lines = block.split('\n').filter(Boolean);
        if (!lines.length) return null;
        if (lines[0].startsWith('### ')) return <h3 key={bi} className="text-lg font-bold text-slate-900 mt-2">{renderInline(lines[0].slice(4))}</h3>;
        if (lines[0].startsWith('## ')) return <h2 key={bi} className="text-2xl font-extrabold text-slate-900 mt-4">{renderInline(lines[0].slice(3))}</h2>;
        if (lines.every(l => l.startsWith('- '))) return (
          <ul key={bi} className="list-disc pl-6 space-y-1.5 text-slate-700 leading-relaxed">
            {lines.map((l, li) => <li key={li}>{renderInline(l.slice(2))}</li>)}
          </ul>
        );
        if (lines.every(l => /^\d+\.\s/.test(l))) return (
          <ol key={bi} className="list-decimal pl-6 space-y-1.5 text-slate-700 leading-relaxed">
            {lines.map((l, li) => <li key={li}>{renderInline(l.replace(/^\d+\.\s/, ''))}</li>)}
          </ol>
        );
        return <p key={bi} className="text-slate-700 leading-relaxed">{renderInline(lines.join(' '))}</p>;
      })}
    </div>
  );
};

const PostCard = ({ p }) => (
  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group rounded-2xl border overflow-hidden bg-white hover:shadow-xl hover:-translate-y-0.5 transition-all">
    {p.cover_image && <div className="aspect-[16/9] overflow-hidden bg-slate-100"><img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" /></div>}
    <div className="p-5">
      <div className="flex flex-wrap gap-1.5">{(p.tags || []).map(t => <span key={t} className="text-[11px] font-semibold bg-orange-50 text-orange-700 border border-orange-200 rounded-full px-2 py-0.5">{t}</span>)}</div>
      <h2 className="mt-3 font-extrabold text-slate-900 leading-snug group-hover:text-orange-600 transition-colors">{p.title}</h2>
      <p className="mt-2 text-sm text-slate-600 line-clamp-3">{p.excerpt}</p>
      <div className="mt-4 flex items-center gap-3 text-xs text-slate-500">
        <span>{fmtDate(p.published_at)}</span>
        <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {p.read_minutes} min read</span>
        <span className="ml-auto text-orange-600 font-semibold inline-flex items-center gap-1">Read <ArrowRight className="h-3 w-3" /></span>
      </div>
    </div>
  </Link>
);

export function BlogListPage() {
  const [posts, setPosts] = useState(null);
  const [tag, setTag] = useState('');

  useEffect(() => { window.scrollTo(0, 0); fetchBlogPosts().then(setPosts).catch(() => setPosts([])); }, []);

  const tags = useMemo(() => [...new Set((posts || []).flatMap(p => p.tags || []))], [posts]);
  const visible = useMemo(() => (posts || []).filter(p => !tag || (p.tags || []).includes(tag)), [posts, tag]);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Seo
        title="Insights & Guides | Hipro Technologies Blog"
        description="Practical guides on software development costs, AI automation ROI, dedicated teams and digital growth — written by the Hipro Technologies engineering team."
        path="/blog"
        jsonLd={[ORG_JSONLD, { '@context': 'https://schema.org', '@type': 'Blog', name: 'Hipro Technologies Insights', url: `${SITE_URL}/blog` }]}
      />
      <Navbar />
      <section className="pt-28 pb-10 bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <span className="text-xs font-semibold uppercase tracking-widest text-orange-400">Insights</span>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold">Guides for building & growing with technology</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">Honest, practical articles on software costs, AI automation, team building and digital growth — from a team that has shipped 120+ projects since 2015.</p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-tag-filters">
              <button onClick={() => setTag('')} className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${!tag ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 hover:border-slate-400'}`}>All</button>
              {tags.map(t => (
                <button key={t} onClick={() => setTag(t === tag ? '' : t)} className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${tag === t ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-600 hover:border-orange-300'}`}>
                  <Tag className="h-3 w-3 inline mr-1 -mt-0.5" />{t}
                </button>
              ))}
            </div>
          )}
          {posts === null ? (
            <div className="py-20 text-center text-slate-500"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
          ) : visible.length === 0 ? (
            <div className="py-20 text-center text-slate-500" data-testid="blog-empty">No articles yet — check back soon.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
              {visible.map(p => <PostCard key={p.id} p={p} />)}
            </div>
          )}
          <div className="mt-12"><NewsletterBox /></div>
        </div>
      </section>
      <Footer />
      <ChatWidget />
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(null); setNotFound(false);
    fetchBlogPost(slug).then(setPost).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="min-h-screen bg-white"><Navbar />
      <div className="max-w-3xl mx-auto px-5 py-40 text-center">
        <h1 className="text-3xl font-extrabold">Article not found</h1>
        <Button onClick={() => nav('/blog')} className="mt-6 bg-orange-500 hover:bg-orange-600">Back to blog</Button>
      </div><Footer /></div>
  );
  if (!post) return <div className="min-h-screen bg-white"><Navbar /><div className="max-w-3xl mx-auto px-5 py-40 text-center text-slate-500"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div></div>;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Seo
        title={`${post.title} | Hipro Technologies Blog`}
        description={(post.excerpt || post.title).slice(0, 160)}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.cover_image}
        jsonLd={[breadcrumbLd([
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: post.title, path: `/blog/${post.slug}` },
        ]), {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.excerpt,
          image: post.cover_image,
          datePublished: post.published_at,
          dateModified: post.updated_at,
          author: { '@type': 'Organization', name: post.author || 'Hipro Technologies' },
          publisher: { '@type': 'Organization', name: 'Hipro Technologies', url: SITE_URL },
          mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
        }]}
      />
      <Navbar />
      <section className="pt-28 pb-10 bg-slate-950 text-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-orange-400"><ArrowLeft className="h-4 w-4" /> All articles</Link>
          <div className="mt-4 flex flex-wrap gap-1.5">{(post.tags || []).map(t => <span key={t} className="text-[11px] font-semibold bg-white/10 border border-white/10 rounded-full px-2 py-0.5">{t}</span>)}</div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold leading-tight" data-testid="blog-post-title">{post.title}</h1>
          <div className="mt-4 flex items-center gap-3 text-sm text-slate-400">
            <span>{post.author}</span><span>·</span><span>{fmtDate(post.published_at)}</span><span>·</span>
            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {post.read_minutes} min read</span>
          </div>
        </div>
      </section>

      {post.cover_image && (
        <div className="max-w-3xl mx-auto px-5 lg:px-8 -mt-0">
          <img src={post.cover_image} alt={post.title} className="w-full rounded-b-2xl sm:rounded-2xl sm:mt-8 aspect-[16/9] object-cover" />
        </div>
      )}

      <article className="py-10">
        <div className="max-w-3xl mx-auto px-5 lg:px-8" data-testid="blog-post-body">
          <Markdown content={post.content} />
          <div className="mt-12 rounded-2xl bg-slate-950 text-white p-8 text-center">
            <h3 className="text-xl font-extrabold">Have a project in mind?</h3>
            <p className="mt-2 text-slate-300 text-sm">Get a detailed estimate within 24 hours — free consultation, no strings attached.</p>
            <Button onClick={() => nav('/book-meeting')} data-testid="blog-cta-btn" className="mt-5 bg-orange-500 hover:bg-orange-600 rounded-full px-6">Book a free call</Button>
          </div>
          <div className="mt-6"><NewsletterBox /></div>
        </div>
      </article>
      <Footer />
      <ChatWidget />
    </div>
  );
}
